import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import {
    refreshOnCrossTabEntitySync,
    refreshOnExternalEntityChange,
    wouldClobberLocalChanges,
} from "@/composables/refresh-on-entity-change";
import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";
import { registerFlush } from "./pending-flush";
import { clearStaleContentOnSwitch } from "./stale-while-revalidate";
import { isAbortError, useLatestRequest } from "./use-latest-request";

export type Role = types.Role;

// Uses the current route to determine what the 'role' should be and fetches
// the role description for you.
export const useRoleStore = defineEntityStore("role", () => {
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();

    const role = ref<Role | undefined>(undefined);
    const router = useRouter();
    const syncStore = useSyncStore();
    const entityId = computed(() => role.value?.identity);
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.ROLE,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.ROLE);

    const { role: roleInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );

    const isFetching = ref(false);
    // Latest-request guard: each updateRole gets its own AbortController +
    // token, so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(role);

    async function updateRole(force = false) {
        const roleId = roleInfo.value?.identity;

        // In case the same role is selected, return early.
        if (!force && role.value && role.value.identity === roleId) {
            return;
        }

        // Start a new request: aborts the previous one, our own signal + token.
        const { token, signal } = begin();

        if (!roleId) {
            description.value = undefined;
            role.value = undefined;
            return;
        }

        isFetching.value = true;
        // Stale-while-revalidate: keep the previously-loaded role + description on
        // screen while the *same* role is refetched, so it doesn't blank the page
        // (the deferred spinner then never flashes). The data overwrites below;
        // updateDescription always refetches for the current role. Switching to a
        // *different* role is handled by clearStaleContentOnSwitch.

        try {
            const { data, response } =
                await api.startDomainRoleMgtGetRoleStartPost({
                    body: {
                        role_id: `${roleId}`,
                    },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "role",
                            id: roleId,
                        },
                    });
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see
            // them. Only a same-role refetch is dropped; navigating to a
            // different role must always commit. A dropped commit
            // self-heals: the edit's own sync re-notifies and refetches.
            if (
                roleId === role.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            role.value = data;
            // The fetched row is the new name baseline. Without this, a
            // server-side rename surfacing on a same-role refetch would read
            // as a local edit (the name manager only re-baselines on
            // identity change) and get echoed back as a save.
            persistName();

            // Fetch description on the same signal/token so an abort or a newer
            // request supersedes it too, and isFetching stays true until it's done.
            await updateDescription(signal, token);
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            // Only the current request owns the fetch flag.
            if (isCurrent(token)) {
                isFetching.value = false;
            }
        }
    }
    // Drop the previous role's content when the route switches to a different
    // role, so stale-while-revalidate never renders the old one on the new page.
    clearStaleContentOnSwitch(
        () => roleInfo.value?.identity,
        [role, description]
    );
    watch(
        () => roleInfo.value?.identity,
        () => updateRole(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (role.value) updateRole(true);
        }
    );

    const updateDescription = async (signal: AbortSignal, token: number) => {
        // Always refetch for the (possibly new) current role — under SWR the
        // previous description stays set, so a `!description.value` guard would
        // wrongly keep the stale value.
        if (role.value) {
            try {
                const descriptionResult = await getDescriptionArtefact(
                    role.value,
                    "roleDescription",
                    signal,
                    revisionQuery.value
                );

                // A newer request superseded us — commit nothing. Same for
                // edits that appeared while fetching (landing-time guard).
                if (
                    !isCurrent(token) ||
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            }
        }
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard can't
    // see them and the refetch's response overwrites the user's text.
    const isModified = computed(() =>
        Boolean(isNameModified.value || description.value?.isModified)
    );

    // One shared opts object: the notification guards and the landing-time
    // commit gates inside the update functions must agree on what "would
    // clobber local changes".
    const externalRefresh = {
        entityType: EntityType.ROLE,
        currentEntityId: () => role.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => updateRole(true),
    };

    refreshOnExternalEntityChange(externalRefresh);

    const queueSave = async () => {
        if (!isModified.value || !role.value) {
            return;
        }

        const updateContent: types.StartDomainRoleMgtUpdateRoleStartPostData = {
            body: {
                role_id: role.value.identity,
            },
            url: "/domain/role_mgt/update_role/start",
        };

        if (isNameModified.value) {
            updateContent.body.name = role.value.name;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            updateContent.body.role_id &&
            Object.keys(updateContent.body).length === 1
        ) {
            // Only role_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.ROLE,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };
    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    refreshOnCrossTabEntitySync({
        ...externalRefresh,
        enabled: () => !!stek.value,
    });

    return {
        role,
        description,
        isFetching,
        isModified,
        reset,
    };
});

export const useRoleManager = () => storeToRefs(useRoleStore());
